import api from './api';

/**
 * Servicio para gestión de paquetes de jornadas PostgreSQL
 * 
 * Conecta el frontend con las APIs de paquetes de jornadas
 * Permite a tutores ver paquetes disponibles y a admins gestionarlos
 */

const paqueteJornadasService = {
  /**
   * Obtener paquetes de jornadas disponibles para compra
   * @param {Object} filters - Filtros opcionales (torneo_id, sede_id)
   * @returns {Promise<Array>} Lista de paquetes disponibles
   */
  async getPaquetesDisponibles(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.torneo_id) {
        params.append('torneo_id', filters.torneo_id);
      }
      
      if (filters.sede_id) {
        params.append('sede_id', filters.sede_id);
      }

      const queryString = params.toString();
      const url = `/postgres/paquetes-jornadas${queryString ? `?${queryString}` : ''}`;
      
      const response = await api.get(url);

      // Asegurar que siempre devolvemos un array
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      // Si no hay data.data, intentar con data directamente
      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }

      console.warn('Respuesta inesperada del servidor:', response.data);
      return [];
    } catch (error) {
      console.error('Error al obtener paquetes de jornadas:', error);

      // Si es un error de red o servidor, devolver array vacío
      if (error.response?.status >= 500 || !error.response) {
        return [];
      }

      throw error;
    }
  },

  /**
   * Obtener torneos activos con sus sedes
   * @returns {Promise<Array>} Lista de torneos activos
   */
  async getTorneosActivos() {
    try {
      const response = await api.get('/postgres/paquetes-jornadas/torneos-activos');

      // Asegurar que siempre devolvemos un array
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      }

      if (response.data && Array.isArray(response.data)) {
        return response.data;
      }

      console.warn('Respuesta inesperada del servidor:', response.data);
      return [];
    } catch (error) {
      console.error('Error al obtener torneos activos:', error);
      
      if (error.response?.status >= 500 || !error.response) {
        return [];
      }
      
      throw error;
    }
  },

  /**
   * Crear nuevo paquete de jornadas (Solo Admin)
   * @param {Object} paqueteData - Datos del paquete
   * @returns {Promise<Object>} Paquete creado
   */
  async crearPaquete(paqueteData) {
    try {
      const response = await api.post('/postgres/paquetes-jornadas', paqueteData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error al crear paquete:', error);
      throw error;
    }
  },

  /**
   * Actualizar paquete de jornadas (Solo Admin)
   * @param {number} id - ID del paquete
   * @param {Object} paqueteData - Datos actualizados
   * @returns {Promise<Object>} Paquete actualizado
   */
  async actualizarPaquete(id, paqueteData) {
    try {
      const response = await api.put(`/postgres/paquetes-jornadas/${id}`, paqueteData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error al actualizar paquete:', error);
      throw error;
    }
  },

  /**
   * Eliminar paquete de jornadas (Solo Admin)
   * @param {number} id - ID del paquete
   * @returns {Promise<Object>} Confirmación de eliminación
   */
  async eliminarPaquete(id) {
    try {
      const response = await api.delete(`/postgres/paquetes-jornadas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar paquete:', error);
      throw error;
    }
  },

  /**
   * Verificar disponibilidad de inscripción para una jornada
   * @param {Date} fechaJornada - Fecha de la jornada
   * @returns {boolean} Si está disponible para inscripción
   */
  verificarDisponibilidadInscripcion(fechaJornada) {
    const ahora = new Date();
    const fechaJornadaObj = new Date(fechaJornada);
    
    // Calcular el miércoles anterior a las 23:59
    const fechaLimite = new Date(fechaJornadaObj);
    const diasHastaMiercoles = (3 - fechaJornadaObj.getDay() + 7) % 7;
    
    if (diasHastaMiercoles === 0) {
      // Si la jornada es miércoles, el límite es el miércoles anterior
      fechaLimite.setDate(fechaJornadaObj.getDate() - 7);
    } else {
      fechaLimite.setDate(fechaJornadaObj.getDate() - diasHastaMiercoles);
    }
    fechaLimite.setHours(23, 59, 59, 999);
    
    return ahora <= fechaLimite;
  },

  /**
   * Formatear precio para mostrar
   * @param {number} precio - Precio en euros
   * @returns {string} Precio formateado
   */
  formatearPrecio(precio) {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(precio);
  },

  /**
   * Calcular precio por jornada
   * @param {number} precio - Precio total del paquete
   * @param {number} cantidadJornadas - Número de jornadas
   * @returns {string} Precio por jornada formateado
   */
  calcularPrecioPorJornada(precio, cantidadJornadas) {
    const precioPorJornada = precio / cantidadJornadas;
    return this.formatearPrecio(precioPorJornada);
  }
};

export default paqueteJornadasService;
